// src/components/OpenNoteModal.js
import React, { useState, useRef, useEffect } from "react";
import "../styles/OpenNoteModal.css";

const OpenNoteModal = ({ onClose, onConfirm, expressOptions = [], productName = "", quantity, count }) => {
  const [selectedChips, setSelectedChips] = useState([]);
  const [customNote, setCustomNote]       = useState("");
  const textareaRef = useRef(null);
  const modalRef = useRef(null);

  // Cerrar con Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Requerimiento "Notas" (móvil): que el teclado virtual no tape el panel.
  // position:fixed se ancla al viewport de layout, que en varios navegadores
  // móviles NO se reduce cuando aparece el teclado (solo el visualViewport
  // sí lo hace) — por eso el panel/footer puede quedar oculto detrás del
  // teclado. Ajustamos la altura máxima del modal al alto real visible
  // (visualViewport.height) cada vez que cambia, para que el campo de texto
  // y los botones Aplicar/Cancelar sigan siempre a la vista. Puramente
  // visual: no toca onConfirm/onClose ni ningún dato.
  const [viewportHeight, setViewportHeight] = useState(null);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const handleViewportChange = () => {
      setViewportHeight(vv.height);
      // Heurística estándar: si el visualViewport queda notablemente más
      // chico que el layout viewport, es porque el teclado está abierto.
      // Solo en ese caso se ajusta el alto del panel — en estado normal
      // no se toca nada y se conserva el 88vh/92vh original del CSS.
      setKeyboardOpen(window.innerHeight - vv.height > 120);
    };
    handleViewportChange();
    vv.addEventListener("resize", handleViewportChange);
    vv.addEventListener("scroll", handleViewportChange);
    return () => {
      vv.removeEventListener("resize", handleViewportChange);
      vv.removeEventListener("scroll", handleViewportChange);
    };
  }, []);

  // Al enfocar el textarea (teclado abriéndose), asegurar que quede a la
  // vista dentro del propio panel (scroll interno, no de la página).
  const handleTextareaFocus = () => {
    setTimeout(() => {
      textareaRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 250);
  };

  const toggleChip = (item) => {
    if (item === "Otra indicación...") {
      textareaRef.current?.focus();
      return;
    }
    setSelectedChips((prev) =>
      prev.includes(item) ? prev.filter((c) => c !== item) : [...prev, item]
    );
  };

  const hasSelection = selectedChips.length > 0 || customNote.trim().length > 0;

  const handleConfirm = () => {
    if (!hasSelection) return;
    const parts = [...selectedChips];
    if (customNote.trim()) parts.push(customNote.trim());
    onConfirm(parts.join(", "));
    onClose();
  };

  // Subtítulo: si hay varios productos seleccionados, se avisa cuántos
  // (requerimiento: "indicando claramente cuántos productos están
  // seleccionados"); si es uno solo, se muestra su nombre y cantidad.
  const subtitle = count > 1
    ? `${count} productos seleccionados`
    : (productName
        ? `${productName}${quantity ? ` · Cant: ${quantity}` : ""}`
        : "");

  return (
    <div
      className="on-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={keyboardOpen && viewportHeight ? { height: `${viewportHeight}px` } : undefined}
    >
      <div
        className="on-modal"
        ref={modalRef}
        style={keyboardOpen && viewportHeight ? { maxHeight: `${Math.max(viewportHeight - 12, 220)}px` } : undefined}
      >

        {/* ── Header ── */}
        <div className="on-header">
          <div className="on-header-icon">
            <i className="bi bi-pencil-square" />
          </div>
          <div className="on-header-text">
            <span className="on-title">Nota del pedido</span>
            {subtitle && <span className="on-subtitle">{subtitle}</span>}
          </div>
          <button className="on-close-btn" onClick={onClose} aria-label="Cerrar">✕</button>
        </div>

        {/* ── Chips por categoría ── */}
        <div className="on-body">
          {expressOptions.length > 0 ? (
            expressOptions.map((cat) => (
              <div key={cat.categoria} className="on-category">
                <span className="on-category-label">{cat.categoria}</span>
                <div className="on-chips">
                  {cat.items.map((item) => {
                    const isCustomTrigger = item === "Otra indicación...";
                    const isSelected = selectedChips.includes(item);
                    return (
                      <button
                        key={item}
                        className={`on-chip${isSelected ? " on-chip--selected" : ""}${isCustomTrigger ? " on-chip--custom" : ""}`}
                        onClick={() => toggleChip(item)}
                        type="button"
                      >
                        {isSelected && <i className="bi bi-check2 on-chip-check" />}
                        {item}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          ) : (
            <p className="on-empty-hint">Sin opciones rápidas configuradas.</p>
          )}

          {/* ── Texto libre ── */}
          <div className="on-custom-area">
            <label className="on-custom-label">
              <i className="bi bi-keyboard" /> Nota personalizada
            </label>
            <textarea
              ref={textareaRef}
              className="on-textarea"
              value={customNote}
              onChange={(e) => setCustomNote(e.target.value)}
              onFocus={handleTextareaFocus}
              placeholder="Escribe cualquier indicación adicional…"
              rows={2}
              maxLength={120}
            />
            {customNote.length > 0 && (
              <span className="on-char-count">{customNote.length}/120</span>
            )}
          </div>

          {/* ── Preview de nota ── */}
          {hasSelection && (
            <div className="on-preview">
              <i className="bi bi-eye" />
              <span>
                {[...selectedChips, customNote.trim()].filter(Boolean).join(", ")}
              </span>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="on-footer">
          <button className="on-btn on-btn--cancel" onClick={onClose} type="button">
            <i className="bi bi-x-lg" /> Cancelar
          </button>
          <button
            className={`on-btn on-btn--confirm${!hasSelection ? " on-btn--disabled" : ""}`}
            onClick={handleConfirm}
            disabled={!hasSelection}
            type="button"
          >
            <i className="bi bi-check-lg" /> Agregar Nota
          </button>
        </div>
      </div>
    </div>
  );
};

export default OpenNoteModal;
